import { useMemo, useRef, useState } from 'react';

export interface TrendSeries {
  key: string;
  label: string;
  color: string;
  values: number[];
  currency?: string;
}

interface TrendChartProps {
  labels: string[];
  series: TrendSeries[];
  valueFormatter: (value: number, currency?: string) => string;
  axisFormatter?: (value: number, currency?: string) => string;
  labelFormatter?: (label: string) => string;
}

const WIDTH = 920;
const HEIGHT = 300;
const PADDING = { top: 20, right: 18, bottom: 38, left: 64 };
const PLOT_WIDTH = WIDTH - PADDING.left - PADDING.right;
const PLOT_HEIGHT = HEIGHT - PADDING.top - PADDING.bottom;
const GRID_LINES = 4;

function niceMax(value: number): number {
  if (value <= 0) return 1;
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  const niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return niceFraction * 10 ** exponent;
}

function smoothPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  const first = points[0];
  if (!first) return '';
  if (points.length === 1) return `M ${first.x} ${first.y}`;

  let path = `M ${first.x} ${first.y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p1 = points[i];
    const p2 = points[i + 1];
    if (!p1 || !p2) continue;
    const p0 = points[i - 1] ?? p1;
    const p3 = points[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = Math.min(
      PADDING.top + PLOT_HEIGHT,
      Math.max(PADDING.top, p1.y + (p2.y - p0.y) / 6)
    );
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = Math.min(
      PADDING.top + PLOT_HEIGHT,
      Math.max(PADDING.top, p2.y - (p3.y - p1.y) / 6)
    );
    path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return path;
}

const TrendChart: React.FC<TrendChartProps> = ({
  labels,
  series,
  valueFormatter,
  axisFormatter,
  labelFormatter,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const maxValue = useMemo(() => {
    let max = 0;
    series.forEach((item) => {
      item.values.forEach((value) => {
        if (Number.isFinite(value) && value > max) max = value;
      });
    });
    return niceMax(max * 1.05);
  }, [series]);

  const points = useMemo(
    () =>
      series.map((item) => ({
        ...item,
        points: item.values.map((value, index) => ({
          x:
            labels.length <= 1
              ? PADDING.left + PLOT_WIDTH / 2
              : PADDING.left + (index / (labels.length - 1)) * PLOT_WIDTH,
          y: PADDING.top + PLOT_HEIGHT - (Math.max(value, 0) / maxValue) * PLOT_HEIGHT,
        })),
      })),
    [series, labels.length, maxValue]
  );

  const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || labels.length === 0) return;
    const ratio = (event.clientX - rect.left) / rect.width;
    const plotRatio = Math.min(
      1,
      Math.max(0, (ratio * WIDTH - PADDING.left) / PLOT_WIDTH)
    );
    setActiveIndex(Math.round(plotRatio * Math.max(labels.length - 1, 0)));
  };

  const labelStep = Math.max(1, Math.ceil(labels.length / 7));
  const activeLabel = activeIndex !== null ? labels[activeIndex] : undefined;
  const activeX =
    activeIndex !== null && labels.length > 0
      ? labels.length <= 1
        ? PADDING.left + PLOT_WIDTH / 2
        : PADDING.left + (activeIndex / (labels.length - 1)) * PLOT_WIDTH
      : null;

  if (labels.length === 0 || series.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-zinc-500 text-sm">
        No data in the selected range
      </div>
    );
  }

  const tooltipStyle: React.CSSProperties =
    activeX === null
      ? {}
      : activeX / WIDTH < 0.18
        ? { left: 0 }
        : activeX / WIDTH > 0.82
          ? { right: 0 }
          : { left: `${(activeX / WIDTH) * 100}%`, transform: 'translateX(-50%)' };

  return (
    <div className="space-y-3">
      {series.length > 1 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {series.map((item) => (
            <div key={item.key} className="flex items-center gap-1.5 text-xs text-zinc-400">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.label}
            </div>
          ))}
        </div>
      )}

      <div
        ref={containerRef}
        className="relative"
        onMouseMove={handleMove}
        onMouseLeave={() => setActiveIndex(null)}
      >
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="w-full h-auto select-none"
          role="img"
          aria-label="Payment trend chart"
        >
          <defs>
            {points.map((item) => (
              <linearGradient
                key={item.key}
                id={`trend-fill-${item.key.replace(/[^a-zA-Z0-9]/g, '')}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={item.color} stopOpacity="0.28" />
                <stop offset="100%" stopColor={item.color} stopOpacity="0" />
              </linearGradient>
            ))}
          </defs>

          {/* Grid + Y axis */}
          {Array.from({ length: GRID_LINES + 1 }, (_, index) => {
            const value = (maxValue / GRID_LINES) * index;
            const y = PADDING.top + PLOT_HEIGHT - (index / GRID_LINES) * PLOT_HEIGHT;
            return (
              <g key={index}>
                <line
                  x1={PADDING.left}
                  x2={PADDING.left + PLOT_WIDTH}
                  y1={y}
                  y2={y}
                  stroke="#27272a"
                  strokeDasharray={index === 0 ? undefined : '4 6'}
                />
                <text
                  x={PADDING.left - 12}
                  y={y + 4}
                  textAnchor="end"
                  className="fill-zinc-500"
                  fontSize="11"
                >
                  {(axisFormatter || valueFormatter)(
                    value,
                    series.length === 1 ? series[0]?.currency : undefined
                  )}
                </text>
              </g>
            );
          })}

          {/* X axis labels */}
          {labels.map((label, index) => {
            if (index % labelStep !== 0 && index !== labels.length - 1) return null;
            const x =
              labels.length <= 1
                ? PADDING.left + PLOT_WIDTH / 2
                : PADDING.left + (index / (labels.length - 1)) * PLOT_WIDTH;
            return (
              <text
                key={`${label}-${index}`}
                x={x}
                y={HEIGHT - 12}
                textAnchor="middle"
                className="fill-zinc-500"
                fontSize="11"
              >
                {labelFormatter ? labelFormatter(label) : label}
              </text>
            );
          })}

          {/* Area (single series only) */}
          {points.length === 1 &&
            points[0] &&
            (() => {
              const item = points[0];
              const line = smoothPath(item.points);
              const first = item.points[0];
              const last = item.points[item.points.length - 1];
              if (!first || !last) return null;
              return (
                <path
                  d={`${line} L ${last.x} ${PADDING.top + PLOT_HEIGHT} L ${first.x} ${
                    PADDING.top + PLOT_HEIGHT
                  } Z`}
                  fill={`url(#trend-fill-${item.key.replace(/[^a-zA-Z0-9]/g, '')})`}
                />
              );
            })()}

          {/* Lines */}
          {points.map((item) => (
            <path
              key={item.key}
              d={smoothPath(item.points)}
              fill="none"
              stroke={item.color}
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}

          {/* Hover guide + dots */}
          {activeX !== null && (
            <line
              x1={activeX}
              x2={activeX}
              y1={PADDING.top}
              y2={PADDING.top + PLOT_HEIGHT}
              stroke="#52525b"
              strokeDasharray="3 4"
            />
          )}
          {activeIndex !== null &&
            points.map((item) => {
              const point = item.points[activeIndex];
              if (!point) return null;
              return (
                <circle
                  key={item.key}
                  cx={point.x}
                  cy={point.y}
                  r="4"
                  fill="#18181b"
                  stroke={item.color}
                  strokeWidth="2.5"
                />
              );
            })}
        </svg>

        {activeIndex !== null && activeLabel && (
          <div
            className="absolute top-0 z-10 pointer-events-none bg-zinc-950/95 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl min-w-[150px]"
            style={tooltipStyle}
          >
            <div className="text-zinc-400 text-[11px] mb-1.5">{activeLabel}</div>
            {series.map((item) => (
              <div key={item.key} className="flex items-center justify-between gap-4 py-0.5">
                <span className="flex items-center gap-1.5 text-xs text-zinc-300">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  {item.label}
                </span>
                <span className="text-xs font-medium text-white">
                  {valueFormatter(item.values[activeIndex] ?? 0, item.currency)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendChart;
